import { Component } from "@theme/component";
import {
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
} from "@theme/utilities";
import { sectionRenderer } from "@theme/section-renderer";
import { morph } from "@theme/morph";
import { ThemeEvents } from "@theme/events";
import { RecentlyViewed } from "@theme/recently-viewed-products";
import {
  DialogCloseEvent,
  DialogOpenEvent,
  DialogComponent,
} from "@theme/dialog";

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = [
    "searchInput",
    "predictiveSearchResults",
    "resetButton",
    "closeModalButton",
  ];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Flag to allow one blur without closing (e.g., clicks on wishlist button)
   * @type {boolean}
   */
  #skipNextBlurClose = false;

  /**
   * Flag to track if modal is in the process of closing to prevent refocusing
   * @type {boolean}
   */
  #isModalClosing = false;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      this.style.setProperty(
        "--predictive-search-results-height",
        `${entry.contentRect.height}px`
      );
    }
  });

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest("dialog-component");
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener("keydown", this.#handleKeyboardShortcut, {
        signal,
      });
      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose,
        { signal }
      );
      dialog.addEventListener(
        DialogOpenEvent.eventName,
        this.#handleDialogOpen,
        { signal }
      );

      this.addEventListener("click", this.#handleModalClick, { signal });
      // Track pointer targets to avoid closing on allowed interactive elements
      document.addEventListener("mousedown", this.#handlePointerDown, {
        signal,
        capture: true,
      });

      // Add blur event listener for modal mode to close dialog when input loses focus
      if (this.refs.searchInput) {
        this.refs.searchInput.addEventListener(
          "blur",
          this.#handleModalInputBlur,
          { signal }
        );
      } else {
      }
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, {
        signal,
      });
      document.addEventListener("click", this.#handleDocumentClick, { signal });
      // Track pointer targets to avoid closing on allowed interactive elements
      document.addEventListener("mousedown", this.#handlePointerDown, {
        signal,
        capture: true,
      });
      if (this.refs.searchInput) {
        this.refs.searchInput.addEventListener("blur", this.#handleInputBlur, {
          signal,
        });
      } else {
      }
    }

    this.#getRecentlyViewed();

    const results = this.refs.predictiveSearchResults.firstElementChild;

    if (results) {
      this.#resizeObserver.observe(results);
    }
  }

  /**
   * Pointer down anywhere: if inside predictive search or on wishlist button, skip next blur-close
   * @param {MouseEvent} event
   */
  #handlePointerDown = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isWishlist = target.closest("wishlist-button") != null ||
      target.closest("open-wishlists-button") != null;
    if (isWishlist || this.contains(target)) {
      this.#skipNextBlurClose = true;
    }
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest("button") ||
      target.closest("a") ||
      target.closest("input");

    // Only refocus if it's not an interactive element and the modal is not in the process of closing
    if (
      !isInteractiveElement &&
      this.refs.searchInput &&
      !this.#isModalClosing
    ) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === "k") {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog open event.
   */
  #handleDialogOpen = () => {
    this.#isModalClosing = false; // Reset the flag when dialog opens
    
    // If there's text in the input, show the reset button and expand search
    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
      this.expandSearch();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#isModalClosing = false; // Reset the flag when dialog closes
    // Don't reset search to preserve input state when modal closes
    // Only collapse the search results
    this.classList.remove("predictive-search--expanded");
  };

  expandSearch = () => {
    this.classList.add("predictive-search--expanded");
    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;
      target.classList.add(`color-${this.dataset.activeColorScheme}`);
    }
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        ".predictive-search-results__wrapper-queries, " +
          ".predictive-search-results__wrapper-products, " +
          ".predictive-search-results__list"
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (
          container.classList.contains(
            "predictive-search-results__wrapper-products"
          )
        ) {
          return Array.from(
            container.querySelectorAll(".predictive-search-results__card")
          );
        }
        return Array.from(
          container.querySelectorAll(
            '[ref="resultsItems[]"], .predictive-search-results__card'
          )
        );
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return (
      this.#allResultsItems?.findIndex(
        (item) => item.getAttribute("aria-selected") === "true"
      ) ?? -1
    );
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove("keyboard-focus");
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute("aria-selected", "true");

        if (this.#isKeyboardNavigation) {
          item.classList.add("keyboard-focus");
        }
        item.scrollIntoView({
          behavior: prefersReducedMotion() ? "instant" : "smooth",
          block: "nearest",
        });
      } else {
        item.removeAttribute("aria-selected");
      }
    }
    // this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      this.#resetSearch();
      return;
    }

    if (
      !this.#allResultsItems?.length ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight"
    ) {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case "ArrowDown":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex =
          currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case "Tab":
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex =
            currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex =
            currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case "ArrowUp":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex =
          currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case "Enter":
        const singleResultContainer =
          this.refs.predictiveSearchResults.querySelector(
            "[data-single-result-url]"
          );
        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector("a")?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set("q", this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } =
      this.refs;

    const allRecentlyViewedElements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add("removing");

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    // Don't refocus if modal is closing to prevent reopening
    if (keepFocus && !this.#isModalClosing) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.expandSearch(); // Ensure search is expanded when typing
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector(
        ".predictive-search-results__inner"
      );
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector(
        ".predictive-search-form__content"
      );
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set("q", searchTerm);
    url.searchParams.set("resources[limit_scope]", "each");

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        // Re-add wishlist buttons after morph
        this.#addWishlistButtons();

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });

    // Fetch total count from search page
    this.#getTotalSearchCount(searchTerm, abortController);
    
    // Show loading state on the button
    this.#showLoadingState();
  }

  /**
   * Fetch total search count from search page
   * @param {string} searchTerm - The term to search for
   * @param {AbortController} abortController - The abort controller for the request
   */
  async #getTotalSearchCount(searchTerm, abortController) {
    try {
      const searchUrl = new URL(Theme.routes.search_url, location.origin);
      searchUrl.searchParams.set("q", searchTerm);
      searchUrl.searchParams.set("type", "product");

      const response = await fetch(searchUrl, {
        signal: abortController.signal,
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Look for search results count in the page
      const resultsCountElement = doc.querySelector('[data-search-results-count]');
      if (resultsCountElement) {
        const totalCount = parseInt(resultsCountElement.getAttribute('data-search-results-count') || '0');
        if (!isNaN(totalCount)) {
          this.#updateTotalCount(totalCount);
        }
      } else {
        // Fallback: look for pagination info or other indicators
        const paginationInfo = doc.querySelector('.pagination-info');
        if (paginationInfo && paginationInfo.textContent) {
          const match = paginationInfo.textContent.match(/(\d+)\s+results?/i);
          if (match && match[1]) {
            const totalCount = parseInt(match[1]);
            if (!isNaN(totalCount)) {
              this.#updateTotalCount(totalCount);
            }
          }
        }
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching total search count:", error);
      }
      // Remove loading state on error
      this.#removeLoadingState();
    }
  }

  /**
   * Show loading state on the view all button
   */
  #showLoadingState() {
    const viewAllButton = this.querySelector('[ref="viewAllButton"]');
    if (viewAllButton) {
      // Add a subtle loading indicator using CSS classes
      viewAllButton.classList.add('predictive-search__loading');
    }
  }

  /**
   * Remove loading state from the view all button
   */
  #removeLoadingState() {
    const viewAllButton = this.querySelector('[ref="viewAllButton"]');
    if (viewAllButton) {
      viewAllButton.classList.remove('predictive-search__loading');
    }
  }

  /**
   * Add or update wishlist buttons to product cards after morph operation
   */
  #addWishlistButtons() {
    // First, clean up any orphaned wishlist buttons
    this.#cleanupOrphanedWishlistButtons();

    // Work per result item to avoid cross-item selection
    const resultItems = this.querySelectorAll('li.predictive-search-results__card--product');

    resultItems.forEach((item) => {
      const card = item.querySelector('product-card[data-product-id]');
      const productId = card?.getAttribute('data-product-id') || undefined;
      const variantId =
        card?.getAttribute('data-variant-id') ||
        card?.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
        undefined;

      if (!productId || !variantId) return;

      let wishlistButton = item.querySelector('wishlist-button');

      if (!wishlistButton) {
        wishlistButton = document.createElement('wishlist-button');
        wishlistButton.className = 'wishlist-button';
        // Insert immediately after the product card inside the same li
        if (card && card.parentNode) {
          card.parentNode.insertBefore(wishlistButton, card.nextSibling);
        } else {
          item.appendChild(wishlistButton);
        }
      }

      // Only update the variant ID, don't replace the element to preserve state
      const currentVariantId = wishlistButton.getAttribute('data-variant-id');
      if (currentVariantId !== variantId) {
        wishlistButton.setAttribute('data-variant-id', variantId);
      }
    });
  }

  /**
   * Clean up orphaned wishlist buttons that don't have corresponding product cards
   */
  #cleanupOrphanedWishlistButtons() {
    const allWishlistButtons = this.querySelectorAll('li.predictive-search-results__card--product wishlist-button');
    allWishlistButtons.forEach((button) => {
      const item = button.closest('li.predictive-search-results__card--product');
      const hasCard = !!item?.querySelector('product-card[data-product-id]');
      if (!hasCard) button.remove();
    });
  }

  /**
   * Update the total count in the view all button
   * @param {number} totalCount - The total count of search results
   */
  #updateTotalCount(totalCount) {
    const viewAllButton = this.querySelector('[ref="viewAllButton"]');
    if (viewAllButton) {
      // Remove loading state
      this.#removeLoadingState();
      
      // Format number with commas
      const formattedCount = totalCount.toLocaleString();
      
      // Add the count to the button text
      const baseText = viewAllButton.textContent.replace(/\s*\([\d,]+\)/, ''); // Remove any existing count (with or without commas)
      viewAllButton.textContent = `${baseText} (${formattedCount})`;
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set(
      "q",
      viewedProducts
        .map(/** @param {string} id */ (id) => `id:${id}`)
        .join(" OR ")
    );
    url.searchParams.set("resources[type]", "product");

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Fetch recently viewed products using the section renderer and update the results container.
   */
  async #getRecentlyViewed() {
    const { predictiveSearchResults } = this.refs;
    // Get the initial height before the results are rendered
    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!resultsMarkup) return;

      const parsedNextPage = new DOMParser().parseFromString(
        resultsMarkup,
        "text/html"
      );
      const recentlyViewedProductsHtml = parsedNextPage.getElementById(
        "predictive-search-products"
      );
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute("ref", "recentlyViewedWrapper");
        }
      }

      const collectionElement = predictiveSearchResults.querySelector(
        "#predictive-search-products"
      );
      if (!collectionElement) return;

      if (this.refs.recentlyViewedWrapper) {
        this.refs.recentlyViewedWrapper.remove();
      }

      if (abortController.signal.aborted) return;
      // Prepend the recently viewed products to the collection
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    } catch (error) {
      throw error;
    }
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = "predictive-search-empty";

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(
      emptySectionId,
      false
    );
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, "text/html")
      .querySelector(".predictive-search-empty-section");

    if (!parsedEmptySectionMarkup)
      throw new Error("No empty section markup found");

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup =
        await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(
        recentlyViewedMarkup,
        "text/html"
      );
      const recentlyViewedProductsHtml =
        parsedRecentlyViewedMarkup.getElementById("predictive-search-products");
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute("ref", "recentlyViewedWrapper");
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector(
        "#predictive-search-products"
      );
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    
    // Re-add wishlist buttons after morph
    this.#addWishlistButtons();
    
    this.#resetScrollPositions();
  };

  /**
   * Closes the predictive search.
   */
  #blurSearch = () => {
    this.refs.searchInput.blur();
  };

  /**
   * Handle clicks outside the predictive search component to close it
   * @param {Event} event - The click event
   */
  #handleDocumentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Check if close button was clicked
    const isCloseButton = target.closest(
      "#predictive-search-products-close-button-trigger"
    );

    // Close if search is expanded and click is outside the component, OR if close button was clicked
    if (
      this.classList.contains("predictive-search--expanded") &&
      (!this.contains(target) || isCloseButton)
    ) {
      this.#closeSearch();
    }
  };

  /**
   * Handle blur events on the search input to close search after a delay
   * @param {Event} event - The blur event
   */
  #handleInputBlur = (event) => {
    // Only handle blur if search is expanded
    if (!this.classList.contains("predictive-search--expanded")) return;

    // Use a small delay to allow for clicks on search results
    setTimeout(() => {
      if (this.#skipNextBlurClose) {
        // consume the flag and keep search open
        this.#skipNextBlurClose = false;
        return;
      }
      // Check if focus moved to an element outside the component
      const activeElement = document.activeElement;
      if (
        !this.contains(activeElement) &&
        !activeElement?.closest("predictive-search-component")
      ) {
        this.#closeSearch();
      }
    }, 200);
  };

  /**
   * Handle blur events on the search input in modal mode to close dialog
   * @param {Event} event - The blur event
   */
  #handleModalInputBlur = (event) => {
    // Use a small delay to allow for clicks on search results or other modal elements
    setTimeout(() => {
      if (this.#skipNextBlurClose) {
        // consume the flag and keep predictive search dialog open
        this.#skipNextBlurClose = false;
        return;
      }
      // Check if focus moved to an element outside the modal
      const activeElement = document.activeElement;
      const dialog = this.dialog;

      // Close dialog if focus moved outside the modal
      if (dialog && !dialog.contains(activeElement)) {
        this.#isModalClosing = true;
        dialog.closeDialog();
      }
    }, 200);
  };

  /**
   * Close the search and reset state
   */
  closeSearch = () => {
    if (this.dialog) {
      // In dialog mode, close the dialog
      this.dialog.closeDialog();
    } else {
      // In dropdown mode, remove expanded class and reset
      this.classList.remove("predictive-search--expanded");
      // this.#resetSearch();
    }
  };

  /**
   * Close the search and reset state (private method for internal use)
   */
  #closeSearch = () => {
    this.classList.remove("predictive-search--expanded");
    // this.#resetSearch();
  };
}

if (!customElements.get("predictive-search-component")) {
  customElements.define(
    "predictive-search-component",
    PredictiveSearchComponent
  );
}
